import type { MuiTheme as Theme } from '@paintscout/ui'
import { Card, makeStyles, Button, Grid, InputField, RadioButtons, Typography } from '@paintscout/ui'
import {
  TagSelection,
  DialogStackContext,
  DropdownSelect,
  useClientOptions,
  useUser,
  PhoneNumberInput,
  PhoneNumberExtensionInput
} from '@paintscout/ui-business'
import type { Company, Contact } from '@paintscout/api'
import { useSearchCompaniesLazyQuery, useSearchCompaniesQuery } from '@paintscout/api'
import { createCompany, getFeatures, getQuoteTags, setOwner } from '@paintscout/util/builder'
import { useQuote } from '@paintscout/react-quote'
import classnames from 'classnames'
import { useSnackbar } from 'notistack'
import type { QuoteTag } from 'paintscout'
import React, { useContext, useEffect, useState } from 'react'

import AddIcon from '@material-ui/icons/Add'
import { isEmailValid, validEmailRegExp } from '@paintscout/util/util'
import { jsonToLucene } from 'json-to-lucene'
import { useFlags } from 'shared/components/common/FlagsProvider'
import { useCompanyMutations } from 'shared/hooks'
import CompanyDialog from '../dialogs/CompanyDialog'
import CompanySelector from '../dialogs/ContactDialog/CompanySelector'
import LeadSourceSelector from '../dialogs/ContactDialog/LeadSourceSelector'
import { useContactAddressSearch } from './useContactAddressSearch'
import { useContactValues } from './useContactValues'
import { useInitialOpenLoad } from './useInitialOpenLoad'

type FormProps = JSX.IntrinsicElements['form']

export interface ContactFormProps extends Omit<FormProps, 'onSubmit'> {
  contact?: Partial<Contact>
  disabled?: boolean
  disableCompanySelector?: boolean
  hideCompany?: boolean
  isSecondary?: boolean

  onSubmit: (contact: Contact) => any
  onDirtyChange?: (dirty: boolean) => void
  onBack?: (event: React.MouseEvent<HTMLElement>) => any
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column'
  },
  section: {
    display: 'flex',
    flexWrap: 'wrap',
    width: '100%',
    justifyContent: 'space-between'
  },
  selectField: {
    marginTop: theme.spacing(1.25)
  },
  textField: {},
  provinceField: {},
  radioButtons: {
    marginTop: theme.spacing(1),
    flexDirection: 'column'
  },
  radioItemLabel: {
    margin: 0
  },
  radioGroup: {
    marginTop: theme.spacing(),
    flexDirection: 'row'
  },
  radio: {
    padding: theme.spacing()
  },
  divider: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  leftActions: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center'
  },
  dropdownRelative: {
    position: 'relative',
    cursor: 'pointer'
  },
  dropdown: {
    zIndex: 21_474_899_999,
    position: 'absolute',
    boxShadow: theme.shadows[2],
    width: '98%'
  },
  dropdownItem: {
    padding: theme.spacing(1, 2),
    '&:hover': {
      outline: '1px solid #e0e0e0',
      backgroundColor: theme.palette.action.hover
    }
  },
  companyEditButton: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    alignItems: 'flex-end',
    height: '100%',
    paddingBottom: theme.spacing(2)
  }
}))

export const contactMethods = [
  { value: 'phone', label: 'Phone' },
  { value: 'email', label: 'Email' },
  { value: 'sms', label: 'Text' }
]

function ContactForm({
  contact,
  disabled,
  disableCompanySelector,
  hideCompany = false,
  isSecondary,
  onSubmit,
  onDirtyChange,
  onBack,
  ...props
}: ContactFormProps) {
  const classes = useStyles({})
  const { user, isSuperadmin, isSupport } = useUser()
  const { openDialog, dismissDialog, dismissAllDialogs } = useContext(DialogStackContext)
  const { saveCompany } = useCompanyMutations()
  const { enqueueSnackbar } = useSnackbar()
  const { quote, updateQuote } = useQuote()
  const { crm: crmFlag } = useFlags()
  const { options } = useClientOptions()
  const features = getFeatures({ options })
  const hasTags = !!(features.quotes?.tags?.enabled || crmFlag)
  const hasSources = !!(features.contactLeadSources?.enabled || crmFlag)

  const requireContactFields = options.options?.requireContactFields

  const quoteTags = getQuoteTags({ options })
  const [query, setQuery] = useState('')
  const {
    contactValues,
    contactSources,
    handleCompanyChange,
    handlePreferredContactMethodChange,
    handleSourceChange,
    handleFieldChange,
    mapAddressToValues,
    handleTagChange,
    isDirty
  } = useContactValues({ contact, options })

  useEffect(() => {
    onDirtyChange?.(isDirty)
  }, [isDirty, onDirtyChange])
  const internationalMode = options?.options?.internationalMode ?? false

  const { isInitialLoad, isOpen, setIsOpen } = useInitialOpenLoad({
    initialLoad: true,
    initialOpen: false
  })

  // const features = getFeatures({ options, preferences })
  // const showContactSources = get(features, 'contactLeadSources.enabled')

  const [search, { loading, data: companiesData }] = useSearchCompaniesLazyQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      query,
      limit: 20,
      sort: ['name']
    }
  })
  const escapedCompany = contactValues.company?.replace(/'/g, "\\'")
  const companyQuery = contactValues.companyDocId ? `id:${contactValues.companyDocId}` : `name:"${escapedCompany}"`
  const { data: currentCompanyData, loading: loadingCompanyData } = useSearchCompaniesQuery({
    variables: {
      query: companyQuery,
      limit: 1,
      sort: ['name']
    },
    fetchPolicy: 'cache-and-network',
    skip: !contactValues?.companyDocId && !escapedCompany?.length
  })
  const [currentCompany, setCurrentCompany] = useState(null)
  const [currentCompanies, setCurrentCompanies] = useState([])
  useEffect(() => {
    if (!currentCompany && !loadingCompanyData && currentCompanyData?.searchCompanies?.companies?.length > 0) {
      setCurrentCompany(currentCompanyData?.searchCompanies?.companies?.[0])
    }
    if (!currentCompanies && !currentCompanies.length && companiesData?.searchCompanies?.companies?.length > 0) {
      setCurrentCompanies(
        companiesData?.searchCompanies?.companies?.map((company) => ({
          value: company._id,
          label: company.name
        }))
      )
    }
  }, [loadingCompanyData])

  useEffect(() => {
    if (loading) {
      setCurrentCompanies([])
      setCurrentCompany(null)
    } else if (companiesData?.searchCompanies?.companies?.length > 0) {
      setCurrentCompanies(
        companiesData?.searchCompanies?.companies?.map((company) => ({
          value: company._id,
          label: company.name
        }))
      )
    }
  }, [loading])

  const { addressSearchResults, debouncedAddressSearch } = useContactAddressSearch({
    options,
    isInitialLoad
  })

  useEffect(() => {
    if (contactValues.address.length > 0 && !isInitialLoad) {
      setIsOpen(true)
      debouncedAddressSearch(contactValues.address)
    }
  }, [contactValues.address])

  async function handleSubmit(event: React.FormEvent<HTMLElement>) {
    event.preventDefault()

    if (!!contactValues.email?.trim() && !isEmailValid(contactValues.email)) {
      enqueueSnackbar('Please enter a valid email address', { variant: 'error' })
      return
    }

    if (!!contactValues.alternate_email?.trim() && !isEmailValid(contactValues.alternate_email)) {
      enqueueSnackbar('Please enter a valid alternate email address', { variant: 'error' })
      return
    }

    // merge contactValues and omit falsey contactValues
    let newContact: Contact = {
      ...contactValues,
      updated_date: Date.now(),
      company: contactValues?.company ?? null,
      companyDocId: contactValues?.companyDocId ?? null
    }

    if (!newContact.owner && !isSuperadmin && !isSupport) {
      newContact = setOwner({ doc: newContact, user })
    }

    Object.keys(newContact).forEach((key) => {
      if (typeof newContact[key] !== 'string') {
        return
      }

      newContact[key] = newContact[key].trim()
    })
    console.log('Submitting contact', { newContact })
    onSubmit(newContact)
  }

  const openCompanyDialog = () => {
    openDialog(CompanyDialog, {
      companyId: currentCompany?._id ?? null,
      onConfirm: async (company: Company) => {
        dismissDialog()
        setCurrentCompany(company)
        handleCompanyChange({
          value: company._id,
          label: company.name
        })
      },
      onCancel: onBack ? dismissAllDialogs : dismissDialog,
      onBack: onBack
    })
  }

  const commonInputProps = {
    onChange: handleFieldChange,
    margin: 'dense' as const,
    fullWidth: true,
    disabled,
    classes: { root: classes.textField }
  }

  return (
    <form className={classes.root} autoComplete="off" onSubmit={handleSubmit} {...props}>
      <div className={classes.section}>
        {isSecondary && (
          <RadioButtons
            classes={{
              root: classes.radioButtons,
              radioLabel: classes.radioItemLabel,
              radioGroup: classes.radioGroup
            }}
            disabled={disableCompanySelector}
            label={'Is this the billing contact for this quote?'}
            onChange={(val) => {
              quote.billingSecondary = val === 'Yes' ? true : false
              updateQuote({ quote })
            }}
            options={[
              { label: 'Yes', name: 'Yes' },
              { label: 'No', name: 'No' }
            ]}
            value={quote.billingSecondary ? 'Yes' : 'No'}
          />
        )}
      </div>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={6}>
          <InputField
            {...commonInputProps}
            required={true}
            error={!contactValues.first_name}
            pattern={{ expression: '(.*[A-Za-z]+.*)', title: 'Name' }}
            id="first_name"
            label="First Name"
            autoComplete={'off'}
            value={contactValues.first_name}
            className={'tour-contact-dialog-first-name'}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <InputField
            {...commonInputProps}
            required={requireContactFields}
            error={requireContactFields && !contactValues.last_name}
            autoComplete={'off'}
            inputProps={{
              role: 'presentation'
            }}
            id="last_name"
            label="Last Name"
            value={contactValues.last_name}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <InputField
            {...commonInputProps}
            className={classnames('contact-secondary-info')}
            autoComplete={'off'}
            inputProps={{
              role: 'presentation'
            }}
            id="secondary_first_name"
            label="Secondary First Name"
            value={contactValues.secondary_first_name}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <InputField
            {...commonInputProps}
            className={classnames('contact-secondary-info')}
            autoComplete={'off'}
            inputProps={{
              role: 'presentation'
            }}
            id="secondary_last_name"
            label="Secondary Last Name"
            value={contactValues.secondary_last_name}
          />
        </Grid>

        <Grid item xs={8} sm={4} md={4}>
          <InputField
            {...commonInputProps}
            required={requireContactFields}
            error={requireContactFields && !contactValues.phone_number}
            inputComponent={PhoneNumberInput}
            inputProps={{
              separator: options?.options?.phoneNumbers?.separator,
              internationalMode,
              role: 'presentation'
            }}
            autoComplete={'tel'}
            id="phone_number"
            label="Phone"
            type="tel"
            value={contactValues.phone_number}
          />
        </Grid>
        <Grid item xs={4} sm={2} md={2}>
          <InputField
            {...commonInputProps}
            inputComponent={PhoneNumberExtensionInput}
            inputProps={{ internationalMode }}
            autoComplete={'off'}
            id="phone_ext"
            label="Ext."
            value={contactValues.phone_ext}
          />
        </Grid>
        <Grid item xs={8} sm={4} md={4}>
          <InputField
            {...commonInputProps}
            inputComponent={PhoneNumberInput}
            inputProps={{
              separator: options?.options?.phoneNumbers?.separator,
              internationalMode,
              role: 'presentation'
            }}
            autoComplete={'off'}
            id="alternate_phone"
            label="Alternate Phone"
            value={contactValues.alternate_phone}
          />
        </Grid>
        <Grid item xs={4} sm={2} md={2}>
          <InputField
            {...commonInputProps}
            inputComponent={PhoneNumberExtensionInput}
            inputProps={{ internationalMode }}
            autoComplete={'off'}
            id="alternate_phone_ext"
            label="Ext."
            value={contactValues.alternate_phone_ext}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <InputField
            {...commonInputProps}
            required={requireContactFields}
            error={requireContactFields && !contactValues.email}
            pattern={{ expression: validEmailRegExp.source, title: 'Email Address' }}
            id="email"
            label="Email"
            value={contactValues.email}
            autoComplete={'email'}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <InputField
            {...commonInputProps}
            pattern={{ expression: validEmailRegExp.source, title: 'Alternate Email Address' }}
            autoComplete={'off'}
            id="alternate_email"
            label="Alternate Email"
            value={contactValues.alternate_email}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={6} className={classes.dropdownRelative}>
          <InputField
            {...commonInputProps}
            autoComplete={'off'}
            inputProps={{
              role: 'presentation'
            }}
            id="address"
            label={'Address'}
            fullWidth={true}
            value={contactValues.address}
            onClick={() => setIsOpen(true)}
            onBlur={() => setIsOpen(false)}
          />
          {addressSearchResults.length > 0 && isOpen && (
            <Card className={classes.dropdown}>
              {addressSearchResults.map((result, i) => {
                const label = [result.address, result.municipality].filter((item) => item).join(', ')
                const label2 = [result.region, result.postalCode].filter((item) => item).join(', ')

                return (
                  <div
                    key={i}
                    onMouseDown={() => {
                      setIsOpen(false)
                      mapAddressToValues(result)
                    }}
                    className={classnames(classes.dropdownItem, `contact-address-dropdown-item-${i}`)}
                  >
                    <Typography variant={'body1'}>{label}</Typography>
                    <Typography variant={'subtitle2'}>{label2}</Typography>
                  </div>
                )
              })}
            </Card>
          )}
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <InputField
            {...commonInputProps}
            autoComplete={'off'}
            id="city"
            inputProps={{
              role: 'presentation'
            }}
            label="City"
            value={contactValues.city}
            onChange={handleFieldChange}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <InputField
            {...commonInputProps}
            autoComplete={'off'}
            id="province"
            label="State / Province"
            inputProps={internationalMode ? {} : { maxLength: 2, role: 'presentation' }}
            value={contactValues.province}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <InputField
            {...commonInputProps}
            autoComplete={'off'}
            inputProps={{
              role: 'presentation'
            }}
            id="postal"
            label="Zip / Postal Code"
            value={contactValues.postal}
          />
        </Grid>
        {hasSources && (
          <Grid item xs={12} sm={6} md={6}>
            <LeadSourceSelector
              classes={{ root: classes.selectField }}
              disabled={false}
              value={{
                value: contactValues.lead_source ? contactValues.lead_source : '',
                label: contactValues.lead_source
                  ? contactSources.find(
                      (source) =>
                        source.label.trim() === contactValues.lead_source.trim() ||
                        source.name === contactValues.lead_source.trim()
                    )?.label
                  : ''
              }}
              sources={contactSources.map((source) => ({
                value: source.name,
                label: source.label
              }))}
              onChange={handleSourceChange}
            />
          </Grid>
        )}
        <Grid item xs={12} sm={hasSources ? 6 : 12} md={hasSources ? 6 : 12}>
          <DropdownSelect
            disabled={disabled}
            classes={{ root: classes.selectField }}
            placeholder={{
              selectPlaceholder: 'Select a preferred contact method'
            }}
            name="preferred_contact_method"
            variant="single"
            label="Preferred Contact Method"
            fullWidth={true}
            value={contactMethods.find((option) => option.value === (contactValues?.preferred_contact_method ?? ''))}
            onChange={handlePreferredContactMethodChange}
            hasUnselect={true}
            options={contactMethods}
            loading={loading}
          />
        </Grid>
        {!hideCompany && (
          <>
            <Grid item xs>
              <CompanySelector
                loading={loading || loadingCompanyData}
                allowCreateOption={false}
                minimumSearchCharacters={1}
                onMinimumSearchCharactersReached={(searchFilter) => {
                  setQuery(searchFilter)
                  const luceneQuery =
                    jsonToLucene({
                      query: searchFilter.replaceAll('-', '')
                    }) || '*:*'
                  search({ variables: { query: luceneQuery } })
                }}
                disabled={disabled || loadingCompanyData}
                value={{
                  value: contactValues.companyDocId ? contactValues.companyDocId : '',
                  label: contactValues.company ? contactValues.company : ''
                }}
                companies={currentCompanies}
                hideDivider
                onChange={async (option) => {
                  if (!option || !option.value) {
                    // remove company
                    setCurrentCompany(null)
                    setCurrentCompanies([])
                    handleCompanyChange(null)
                    return
                  }
                  // find this company in the companiesData results and use it as currentCompany
                  const selectedCompany = companiesData?.searchCompanies?.companies?.find(
                    (company) => company._id === option.value
                  )
                  // console.log('selectedCompany', selectedCompany)
                  if (selectedCompany) {
                    setCurrentCompany(selectedCompany)
                    handleCompanyChange(option)
                  } else {
                    // new custom company
                    try {
                      const newCompany = createCompany(
                        {
                          name: option.value
                        },
                        options
                      ) as Company
                      const saveResult = await saveCompany({ company: newCompany })
                      const savedCompany = saveResult.company
                      setCurrentCompany(savedCompany)
                      handleCompanyChange({
                        value: savedCompany._id,
                        label: savedCompany.name
                      })
                      setCurrentCompanies([...currentCompanies, { value: savedCompany._id, label: savedCompany.name }])
                    } catch (error) {
                      console.log(error)
                      enqueueSnackbar('Unable to create company', { variant: 'error' })
                    }
                  }
                }}
                hasUnselect={true}
              />
            </Grid>
            <Grid item xs={'auto'}>
              <div className={classes.companyEditButton}>
                <Button
                  icon={currentCompany ? null : AddIcon}
                  variant="dialog"
                  color="primary"
                  size={'medium'}
                  onClick={openCompanyDialog}
                  disabled={loadingCompanyData}
                  inputHeight
                >
                  {!currentCompany ? 'New' : 'Edit'}
                </Button>
              </div>
            </Grid>
          </>
        )}
        {hasTags && (
          <Grid item xs={12}>
            <TagSelection
              label="Tags"
              className="contact-form-tags-dropdown"
              popperPlacement="top"
              appliedTags={
                contactValues?.tags?.map((tag: string) => {
                  // grab the right tag from quoteTags
                  const quoteTag = quoteTags.find((quoteTag) => quoteTag.name === tag)
                  return quoteTag
                }) || []
              }
              fullWidth
              handleChange={(updatedTags: QuoteTag[]) => {
                handleTagChange(updatedTags)
              }}
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <InputField
            {...commonInputProps}
            style={{ marginTop: 0 }}
            id="internal_note"
            label="Internal Note"
            multiline={true}
            rows={5}
            value={contactValues.internal_note}
          />
        </Grid>
      </Grid>
    </form>
  )
}

export default ContactForm
